import type { EncodedAudioData, EncodedAudioFrame, EncodedData, EncodedVideoData, EncodedVideoFrame } from '../mpegts/demuxerTypes';
import type { Logger } from '../types/logger';
import { invariant } from '../util/invariant';
import { Mp4Reader, type TrackFragmentBaseMediaDecodeTimeBox, type TrackFragmentHeaderBox, type TrackRunBox } from './parser';
import type { Mp4Info, Mp4ProgressiveStatus } from '@mimir/common/mp4/mp4Types';
import { inspectMoov } from './inspectMoov';
import { FRAC_ZERO } from '../util/fractionType';
import { safeArrayAccess } from '../util/safeArrayAccess';
import { isProgressiveH264FrameFromMp4 } from '../h264/isProgressiveH264FrameFromMp4';


export function demuxFmp4Segment(logger: Logger, mp4Info: Mp4Info, fmp4SegmentBytes: Uint8Array): EncodedData {
  return new FragmentedMp4Demuxer(logger, mp4Info, fmp4SegmentBytes).parse();
}



export function decodeFmp4InitSegment(fmp4InitSegmentBytes: Uint8Array, ignoreVideo: boolean, ignoreAudio: boolean): Mp4Info {
  const boxes = new Mp4Reader(fmp4InitSegmentBytes).parse();
  const moovBox = boxes.find(b => b.type === 'moov');
  invariant(moovBox, 'Could not find moov box');
  return inspectMoov(undefined, moovBox, undefined, ignoreVideo, ignoreAudio);
}

interface TentativeEncodedVideoData {
  partialData: Omit<EncodedVideoData, 'progressive'>;
  progressiveStatus: Mp4ProgressiveStatus;
}

class FragmentedMp4Demuxer {

  constructor(
    private logger: Logger,
    private info: Mp4Info,
    private fmp4SegmentBytes: Uint8Array,
  ) {}


  private prepareVideoData(): TentativeEncodedVideoData | undefined {
    if (this.info.videoTracks.length === 0) {
      return undefined;
    }

    if (this.info.videoTracks.length !== 1) {
      this.logger.warn(`Got ${this.info.videoTracks.length} video tracks, only retaining th first one`);
    }

    const videoTrack = safeArrayAccess(this.info.videoTracks, 0);
    return {
      partialData: {
        codec: videoTrack.codecString,
        width: videoTrack.width,
        height: videoTrack.height,
        description: videoTrack.avcC,
        timescale: videoTrack.timescale,
        pixelAspectRatio: videoTrack.pixelAspectRatio,
        epsilon: FRAC_ZERO,
        frameDuration: 0,
        colorSpace: null,
        frames: [],
        // The mp4 container format has support for exact frame duration,
        // so we can assert true here, because we explicitly reject (at least for now)
        // videos with a variable framerate
        exactFrameDuration: true,
      },
      progressiveStatus: videoTrack.progressiveStatus,
    };
  }


  private prepareAudioData(): EncodedAudioData[] {
    return this.info.audioTracks.map<EncodedAudioData>(audioTrack => ({
      // TODO: can we figure out the audio object type from the moov
      //       instead of harcoding 2 for AAC LC ?
      codec: 'mp4a.40.2',

      // This is not needed for decoding with the webcodec API
      channelConfiguration: -1,
      numberOfChannels: audioTrack.channelCount,
      sampleRateInHz: audioTrack.sampleRate,
      timescale: audioTrack.timescale,
      epsilon: FRAC_ZERO,
      frames: [],
    }));
  }

  public parse(): EncodedData {
    const boxes = new Mp4Reader(this.fmp4SegmentBytes).parse();
    const videoTrackId = this.info.videoTracks[0]?.trackId;
    const audioTrackId2Index: Record<number, number> = {};
    for (let i = 0; i < this.info.audioTracks.length; i++) {
      audioTrackId2Index[this.info.audioTracks[i]!.trackId] = i;
    }

    const tentativeEncodedVideoData = this.prepareVideoData();
    const encodedVideoData = tentativeEncodedVideoData?.partialData;
    const encodedAudioData = this.prepareAudioData();

    let boxIndex = 0;
    while (boxIndex < boxes.length) {
      if (boxes[boxIndex]!.type === 'moof') {
        const moofBox = boxes[boxIndex];
        let defaultBaseDataOffset = moofBox?.start;

        moofBox?.children.forEach(b => {
          if (b.type === 'traf') {

            const trafBox = b;
            const tfhdBox = trafBox.children.find(bb => bb.type === 'tfhd') as TrackFragmentHeaderBox | undefined;
            invariant(tfhdBox, 'Could not find tfhd box');

            // oxlint-disable-next-line no-bitwise

            const tfdtBox = trafBox.children.find(bb => bb.type === 'tfdt') as TrackFragmentBaseMediaDecodeTimeBox | undefined;
            let baseMediaDecodeTime = tfdtBox?.baseMediaDecodeTime ?? 0;
            const trunBoxes = trafBox.children.filter(bb => bb.type === 'trun') as TrackRunBox[];

            trunBoxes.forEach(trunBox => {
              const baseOffset = tfhdBox.base_data_offset ?? defaultBaseDataOffset;

              let currentDts = baseMediaDecodeTime;
              if (tfhdBox.track_ID === videoTrackId) {
                invariant(encodedVideoData, 'Should have encodedVideoData');

                if (encodedVideoData.frameDuration === 0) {
                  // If we don't already know the frame duration, let's figure it out.
                  // We have observed on some files that sometimes, both the default sample duration
                  // and the duration of the first sample are different than the rest of the samples.
                  // So, if we find an explicit duration for the second second, let's use this value
                  if (trunBox.samples.length > 1 && trunBox.samples[1]?.sample_duration !== undefined) {
                    encodedVideoData.frameDuration = trunBox.samples[1].sample_duration;
                  } else {
                    invariant(tfhdBox.default_sample_duration !== undefined, 'Should have default_sample_duration on tfhd box');
                    encodedVideoData.frameDuration = tfhdBox.default_sample_duration;
                  }
                }
                const frameDuration = encodedVideoData.frameDuration;

                // Processing a video track
                invariant(baseOffset !== undefined, 'Should have baseOffset');
                let start = baseOffset + (trunBox.data_offset ?? 0);
                for (let i = 0; i < trunBox.samples.length; i++) {
                  const keyframe = i === 0 || trunBox.first_sample_flags === undefined;
                  const sample = safeArrayAccess(trunBox.samples, i);
                  if (encodedVideoData.frameDuration === 0 && sample.sample_duration !== undefined) {
                    encodedVideoData.frameDuration = sample.sample_duration;
                  }

                  const sampleSize = sample.sample_size ?? tfhdBox.default_sample_size;
                  invariant(sampleSize !== undefined, 'Should have sample size');
                  const end = start + sampleSize;

                  if (baseMediaDecodeTime === 0 && i === 0 && sample.sample_duration !== undefined && sample.sample_duration !== frameDuration) {
                    // The first sample does not have the same duration as the other ones.
                    // We need to adjust for it
                    baseMediaDecodeTime += (sample.sample_duration - frameDuration);
                    currentDts = baseMediaDecodeTime;
                  }
                  const frame: EncodedVideoFrame = {
                    pts: currentDts + (sample.sample_composition_time_offset ?? 0),
                    dts: currentDts,
                    keyframe,
                    data: this.fmp4SegmentBytes.slice(start, end),
                  };
                  encodedVideoData.frames.push(frame);

                  start = end;
                  defaultBaseDataOffset = end;
                  invariant(encodedVideoData.frameDuration !== 0, 'Should have known frame duration');
                  currentDts += frameDuration;
                }
              } else {
                const audioIndex = audioTrackId2Index[tfhdBox.track_ID];
                if (audioIndex !== undefined) {
                  // Processing an audio track
                  invariant(baseOffset !== undefined, 'Should have baseOffset');
                  let start = baseOffset + (trunBox.data_offset ?? 0);
                  for (let i = 0; i < trunBox.samples.length; i++) {
                    const sample = safeArrayAccess(trunBox.samples, i);
                    const sampleSize = sample.sample_size ?? tfhdBox.default_sample_size;
                    invariant(sampleSize !== undefined, 'Should have sample size');
                    const end = start + sampleSize;

                    let sampleDuration = sample.sample_duration ?? 1024;
                    if (i === 0 && sample.sample_duration !== undefined && sample.sample_duration > 1024) {
                      // If the first sample range is reported with a duration larger than the regular
                      // 1024 samples, we adjust the DTS/PTS to make it start 1024 samples before the next
                      // sample range
                      currentDts += sample.sample_duration - 1024;
                      sampleDuration = 1024;
                    }
                    const frame: EncodedAudioFrame = {
                      duration: sampleDuration,
                      dts: currentDts,
                      pts: currentDts,
                      keyframe: true,
                      data: this.fmp4SegmentBytes.slice(start, end),
                    };
                    start = end;
                    defaultBaseDataOffset = end;
                    encodedAudioData[audioIndex]!.frames.push(frame);

                    currentDts += 1024;
                  }
                }
              }
            });
          }
        });
      }
      boxIndex++;
    }

    let completeEncodedVideoData: EncodedVideoData | undefined;
    if (tentativeEncodedVideoData) {
      let progressive: boolean;
      const progressiveStatus = tentativeEncodedVideoData.progressiveStatus;
      switch (progressiveStatus) {
        case 'progressive': progressive = true; break;
        case 'interlaced': progressive = false; break;
        case 'need-to-inspect-nal_units': {
          const videoTrack = this.info.videoTracks[0];
          invariant(videoTrack, 'Should have video track');
          invariant(videoTrack.frameInfo, 'Should have frame info for video track');
          const frame = tentativeEncodedVideoData.partialData.frames[0];
          invariant(frame, 'Should have at least one video frame');
          progressive = isProgressiveH264FrameFromMp4(this.logger, videoTrack.frameInfo, videoTrack.nalLengthSize, frame.data);
          // Let's update the mp4 info to avoid having to redo this analysis later
          videoTrack.progressiveStatus = progressive ? 'progressive' : 'interlaced';
          break;
        }
        default: progressiveStatus satisfies never; throw new Error(`Unsupported progressive status: ${progressiveStatus}`);
      }

      completeEncodedVideoData = {
        ...tentativeEncodedVideoData.partialData,
        progressive,
      };
    }

    return {
      encodedVideoData: completeEncodedVideoData,
      encodedAudioData,
    };
  }
}
